import {ProductWidget} from '@/lib/TypesOpenApi';
import {DeepReadonly} from '@/lib/typings/readonly';
import {proxy, subscribe} from 'valtio';
import {
  PointSelectorPageWidget,
  pointSelectorWidget,
} from '../PointSelectorStore';
import {deepClone} from '@/lib/vatio-persistence/objectManagment';
import {sum} from './calc';
import {findProducts, findProduct} from './product';

export interface PointsState {
  /** Included products added by back-end and free */
  included: Array<ProductWidget>;
  /** Included product quantity can be less than default, but can't be more */
  defaultIncludedQuantity: {[productId: string]: number};
  /** Additional products choosed by user and must be paid */
  additional: Array<ProductWidget>;
  /** Additional product that appears on CPE page */
  additionalProduct?: ProductWidget;

  /** Merged array of included and additional products */
  all: Array<ProductWidget>;
  /** Maximum number of included product. Appear on cpe page */
  maxIncluded?: number;

  /** Collect and find all products in included and additional section */
  findProduct: (productId: string) => Array<ProductWidget>;
  /** Total count for specific product */
  totalCount: (productId: string) => number;
}

// if add any new field, add the new field to the below reset function also
export const points = proxy<PointsState>({
  included: [],
  defaultIncludedQuantity: {},
  additional: [],
  maxIncluded: undefined,

  get all() {
    // Creating new object reqiures
    return proxy([...this.included, ...this.additional]);
  },

  findProduct(produictId: string): Array<ProductWidget> {
    // Using of this is crucial
    return findProducts(this.all, produictId);
  },

  totalCount(produictId: string): number {
    // Using of this is crucial
    const products: Array<ProductWidget> = this.findProduct(produictId);
    return products.map(({quantity}) => quantity).reduce(sum, 0);
  },
});

export const resetPoints = () => {
  points.included = [];
  points.defaultIncludedQuantity = {};
  points.additional = [];
  points.additionalProduct = undefined;
};

/**
 * Will increase included product count if it less than default quantity.
 * If included product quanitity already on maximum will attach additional product.
 * if Additional product exists, will increase it
 * */
export const increasePointCount = (productId: string) => {
  const includedProduct = findProduct(points.included, productId);
  const defaultIncludedQuantity = points.defaultIncludedQuantity[productId];
  if (!includedProduct || !defaultIncludedQuantity) {
    // expect that included product allways exists in such situation
    console.error(
      "Unexpected state, included product doesn't exists or not have default quanitity. Cannot increase it",
      productId,
    );
    return;
  }

  if ((includedProduct.quantity || 0) < defaultIncludedQuantity) {
    // included product less than maximum, will modify
    includedProduct.quantity = (includedProduct.quantity || 0) + 1;
    return;
  }

  // included product reached maxim, need increase additional
  const additionalProduct = findProduct(points.additional, productId);
  if (!additionalProduct) {
    // not have additional product, will attach such
    points.additional.push({
      ...points.additionalProduct,
      quantity: 1,
    } as ProductWidget);
    return;
  }

  // aditional product already exists, will increase it
  additionalProduct.quantity = (additionalProduct.quantity || 0) + 1;
};

export const decreasePointCount = (productId: string) => {
  // will try to decrease additional product at first, then included
  const allProducts = [...points.additional, ...points.included];
  for (const p of allProducts) {
    if (p.productId !== productId) {
      continue;
    }

    if ((p?.quantity || 0) > 0) {
      p!.quantity!--;
      // found product that can be modified, not need to modify other instances
      return;
    }
  }
};

/**
 * includedPointProduct is set in useLoadStep.ts after leaving internet plan page
 */
export const onPointWidgetChange = (widget: PointSelectorPageWidget) => () => {
  /** need this check to avoid re-initializing points and breaking counting logic */
  if (points.additionalProduct) {
    return;
  }

  const {productWidget} = widget;
  points.additionalProduct = productWidget?.productWidget;
  points.maxIncluded = productWidget?.includedCount;

  const includedProduct = points.included.find(
    p => p.productId === productWidget?.productWidget?.productId,
  );
  if (includedProduct) {
    includedProduct.quantity = productWidget?.defaultCount;
    /** on this step we will have points.maxIncluded */
    points.defaultIncludedQuantity[productWidget?.productWidget?.productId!] =
      points.maxIncluded!;
  }
  /**
   * there is a case when internet plan does not include router by default
   * in that case we set it here
   */
  const {productId} = productWidget?.productWidget || {};
  if (productId && !findProduct(points.included, productId)) {
    setIncludedPointProduct(productWidget?.productWidget!);
    return;
  }
};
subscribe(pointSelectorWidget, onPointWidgetChange(pointSelectorWidget));

// will add product to included or modify if it exists
export const setIncludedPointProduct = (
  product: DeepReadonly<ProductWidget>,
) => {
  // cloning allow to make product modifiable, even if it was locked
  const modifiable = deepClone(product) as ProductWidget;
  /** two cases: setIncluded count on cpe page or on leaving internet page  */
  points.defaultIncludedQuantity[modifiable.productId!] =
    points.maxIncluded || modifiable.quantity || 0;

  const index = points.included.findIndex(
    p => p.productId === modifiable.productId,
  );
  if (index !== -1) {
    points.included[index] = modifiable;
    return;
  }

  points.included.push(modifiable);
};

/** Will remove target routers if found them */
export const removeIncludedPointProducts = (remove: Array<ProductWidget>) => {
  // will try to remove what can, and track to check if something were left
  let removedCount = 0;
  for (const p of remove) {
    const includedProductIndex = points.included.findIndex(
      ({productId}) => p.productId === productId,
    );

    if (includedProductIndex === -1) {
      console.warn(
        "Canntot remove point product, wasn't found in included products array",
        p.productId,
      );
      continue;
    }

    points.included.splice(includedProductIndex, 1);
    points.defaultIncludedQuantity[p.productId!] = 0;
    removedCount++;
  }

  return removedCount;
};
